import { useNavigate } from 'react-router-dom'
import { useStore } from '../../store'
import { Icon } from '../ui'
import { InitialsMenu } from './InitialsMenu'
import { SearchBox } from './SearchBox'
import { SignedInPeers } from './SignedInPeers'
import { StatusChip } from './StatusChip'

/**
 * The console header — docs/design-handoff/tossker-redesign/README.md §1
 * "Header" and §"Layout — rails": fixed 60px, `--surface-card`, a 1px
 * `--border-subtle` bottom border, 20px side padding. Left to right: the
 * brand mark + wordmark, the search field, the service-status chip, then a
 * `margin-left:auto` group of the signed-in peer avatars, a divider, and the
 * account menu.
 *
 * THE BELL (fix round 1, owner override of binding decision 3). Andrew: "just
 * because there isn't one today doesn't mean I don't have one coming" — a
 * notification centre is planned, and this is the seam for it. The original
 * objection stands in one respect and shapes how it's built: a control that
 * does nothing when clicked is still a lie about capability. So this is a
 * real `<button>`, accessible name "Notification settings", that navigates
 * to Settings → Personal preferences — the pane `p.sound`/`p.desktop`/
 * `p.push`/`p.preview` already live on. That is a true statement about what
 * clicking it does today. It carries NO badge/dot/count — there is nothing
 * yet to count, and an always-empty badge would be exactly the same lie in a
 * different shape. When a real notification centre exists, this is the
 * button to repoint — do not add a second bell beside it.
 *
 * Same reasoning still removed the README's "⌘K hint" chip that would
 * otherwise sit on the search field: there is no keyboard shortcut wired to
 * open it, so a hint chip would promise one that doesn't exist.
 *
 * ALSO REMOVED (facelift phase 1, kept removed here): the ☀/🌙 theme
 * toggle. Appearance is a stored personal preference now — `p.theme` in
 * Settings → Personal preferences — with six choices rather than two.
 *
 * REMOVED FROM HERE (users-and-permissions design doc §6, unchanged by this
 * task): the self-service `AdminCheckbox` and `RoleSegment`. Role is
 * admin-assigned now, through the real Users pane
 * (`src/components/settings/UsersSection.tsx`, `PATCH /ops/users/:id`)
 * inside Settings.
 *
 * MOVED OUT OF HERE (task-2 brief, binding decision 6): `CoverageToggle`.
 * The header used to render the coverage pill + switch directly; it now
 * lives inside `InitialsMenu`'s state block ("Covering the queue"), with
 * `ops.setCoverage`'s exact call/wait/write-store-on-success/toast-either-way
 * behaviour untouched — only its location moved. "Am I covering" still
 * shows in the header, as the green dot `InitialsMenu` draws on the account
 * avatar.
 *
 * The wordmark reads `cfg['b.name']`, never a literal — see
 * `deploy/test/no-literals.test.mjs`, which fails the build on a hardcoded
 * product name anywhere in `apps/console/src`.
 */
export function Header() {
  const nameFromCfg = useStore((s) => s.cfg['b.name'])
  const openCompose = useStore((s) => s.openCompose)
  const set = useStore((s) => s.set)
  const navigate = useNavigate()

  const wordmark = typeof nameFromCfg === 'string' && nameFromCfg.length > 0 ? nameFromCfg : 'Console'

  const goNotificationSettings = () => {
    set({ page: 'settings', settingsTab: 'personal' })
    navigate('/settings')
  }

  return (
    <header
      data-testid="shell-header"
      // `relative` so the phone's expanded search can sit OVER this row
      // rather than inside it — see SearchBox, and the width arithmetic in
      // its header comment for why sharing the row is impossible at 390pt.
      className="relative flex h-[60px] items-center gap-3 border-b border-subtle bg-card px-4 md:gap-4 md:px-5"
    >
      {/* Shrinkable on a phone, fixed on a desktop. The wordmark comes from
          config and can be any length, so on a narrow screen it truncates
          instead of shouldering the rest of the header off the display —
          which is exactly what used to happen. */}
      <div className="flex min-w-0 items-center gap-2 md:flex-none">
        <img src="/brand-mark.svg" alt="" aria-hidden="true" width={26} height={26} className="flex-none" />
        <span className="relative inline-flex min-w-0 flex-col">
          {/* README §1: --teal-800, which is --brand-primary-text (`text-cc-blue-text`)
              here — --brand-primary (`text-cc-blue`) is --teal-700, one step lighter. */}
          <span className="truncate font-display text-[19px] font-semibold tracking-[-0.04em] text-cc-blue-text">
            {wordmark}
          </span>
          {/* The gold rule beside the wordmark, README §1 — a fixed --gold-400
              in both Tossker themes (tokens.css's --brand-rule), decorative,
              so it carries no text of its own for deploy/test/no-literals.test.mjs
              to have an opinion about. */}
          <span aria-hidden="true" className="ml-[32%] h-[1.5px] w-[15px] rounded-full bg-brand-rule" />
        </span>
      </div>
      <SearchBox />
      <StatusChip />
      <div data-testid="shell-right-group" className="ml-auto flex flex-none items-center gap-3">
        {/* Outbound composer entry. Andrew moved this up here from its first home
            (a strip in the queue rail — "it feels weird. I think it should be up
            on the very top bar", 2026-08-23): starting a conversation is a
            console-level act, not a queue-filter one. Same testid so nothing
            downstream re-learns the name. */}
        <button
          type="button"
          data-testid="rail-new-conversation"
          onClick={() => openCompose()}
          className="cursor-pointer whitespace-nowrap rounded-lg border border-cc-blue px-[11px] py-[7px] text-[11px] font-semibold text-cc-blue-text"
        >
          New conversation
        </button>
        <span aria-hidden="true" className="h-6 w-px border-l border-subtle" />
        <SignedInPeers />
        <span aria-hidden="true" className="h-6 w-px border-l border-subtle" />
        <button
          type="button"
          onClick={goNotificationSettings}
          aria-label="Notification settings"
          className="text-lo hover:text-hi"
        >
          <Icon name="bell" size={19} />
        </button>
        <InitialsMenu />
      </div>
    </header>
  )
}
